/**
 * Task card on the Kanban board.
 *
 * Layout per Claude Design Files README section 5.2, with the UAT
 * round-2 / round-3 adjustments landed in PR #31, PR #32, and PR #35:
 *   - dark card surface (`mx-card`) one shade above the shell so the
 *     card reads as a separate plane against `mx-near-black`
 *   - 1px grey hairline border, no shadow, 2px radius
 *   - 13.5px title (Inter 500)
 *   - meta row: filled type tag + due chip
 *   - frog indicator: 2px orange top bar when the task is frogged today
 *   - whole-card priority tint: the body background carries the
 *     priority colour (CRITICAL warm red, HIGH warm orange, MEDIUM
 *     neutral, LOW cool grey). PR #31 used a 3px left rail; PR #35
 *     replaced it with the body tint because the rail was too narrow
 *     to register against the dark column gutters.
 *   - selected state: orange border + inset orange ring
 *   - auto-pulled state: brief orange flash + AUTO-PULLED chip
 *   - sales-queue spawn (PR #32): faint orange body wash so the kanban
 *     viewer can tell at a glance which rows came from the daily queue
 *
 * The card is sortable via dnd-kit; the surrounding Column wires the
 * sortable context and ID generation.
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Task, TaskPriority } from '@workspace/shared';

import { cn, todayIso } from '@/lib/utils';
import { TypePill } from './pills';

interface TaskCardProps {
  task: Task;
  /** When true, the card renders the brief auto-pulled flash + chip. */
  autoPulled?: boolean;
  /** Called when the user clicks the card to open the slide-over. */
  onClick: (taskId: string) => void;
}

function isFrogToday(task: Task): boolean {
  const today = todayIso();
  return task.frogDates.includes(today);
}

function dueLabel(task: Task): string | null {
  if (!task.dueDate) return null;
  const today = todayIso();
  if (task.dueDate === today) return 'TODAY';
  return task.dueDate.slice(5); // MM-DD
}

/**
 * Whole-card background tint per priority. PR #35 replaced the 3px
 * left rail with a body tint after Peter flagged that the rail was
 * too narrow to register against the dark column gutter. PR #38
 * swapped the inline `bg-[#...]` hexes for the canonical
 * `mx-priority-*` Tailwind tokens so the palette overhaul lives in a
 * single place and the table pills can share the exact same colour
 * map. CRITICAL lands on a dark muted red; HIGH on a proper dark
 * orange (PR #38 dropped the previous mustard-leaning brown); MEDIUM
 * on a dark muted navy so the bulk of the board reads as cool but
 * calm; LOW on the shell-adjacent neutral so it visibly steps below
 * MEDIUM. Title and chip text stay in the light-grey / white range;
 * the tint is the only contrast cue.
 */
const PRIORITY_BODY: Record<TaskPriority, string> = {
  CRITICAL: 'bg-mx-priority-critical',
  HIGH: 'bg-mx-priority-high',
  MEDIUM: 'bg-mx-priority-medium',
  LOW: 'bg-mx-priority-low',
};

export function TaskCard({ task, autoPulled, onClick }: TaskCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: { type: 'task', status: task.status },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  } as const;

  const frog = isFrogToday(task);
  const due = dueLabel(task);

  return (
    <article
      ref={setNodeRef}
      style={style}
      data-testid="task-card"
      data-task-id={task.id}
      data-source={task.source}
      data-priority={task.priority}
      onClick={() => onClick(task.id)}
      {...attributes}
      {...listeners}
      className={cn(
        'group relative cursor-pointer select-none border border-mx-grey-800 p-mx-3 transition-colors duration-mx-default ease-mx-default hover:border-mx-grey-700',
        PRIORITY_BODY[task.priority],
        isDragging && 'opacity-40',
        autoPulled && 'ring-1 ring-mx-orange/60',
      )}
    >
      {frog && (
        <span
          aria-hidden
          data-testid="frog-bar"
          className="absolute left-0 right-0 top-0 h-[2px] bg-mx-orange"
        />
      )}
      {autoPulled && (
        <span
          data-testid="auto-pulled-chip"
          className="mono-label absolute right-mx-3 top-mx-3 bg-mx-orange px-mx-2 py-[2px] text-mx-mono-sm text-mx-white"
        >
          AUTO-PULLED
        </span>
      )}
      <h3
        data-testid="task-card-title"
        className={cn(
          'text-mx-body-sm font-medium text-mx-grey-300',
          // UAT-45 strikethrough on DONE cards. The line-through reads
          // as a visual completion cue on the kanban Board's daily
          // DONE column; moving the card back to any other status
          // re-renders without the modifier.
          task.status === 'DONE' && 'line-through opacity-65',
        )}
      >
        {task.title}
      </h3>
      <div className="mt-mx-2 flex items-center gap-mx-2">
        <TypePill value={task.type} />
        {due && (
          <span className="mono-label text-mx-grey-mid">
            DUE <span className="text-mx-grey-300">{due}</span>
          </span>
        )}
      </div>
    </article>
  );
}
